import {AfterViewInit, Component, inject, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {Organisation, OrganisationControllerService} from "../../../../api";
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from "@angular/forms";
import {Subject} from "rxjs";
import {PermissionService} from "../../../../services/permission/permission.service";
import {StateConfigService} from "../../../../services/state/state-config.service";
import {Router} from "@angular/router";
import { KeyValuePipe, NgTemplateOutlet } from "@angular/common";

import {LegalFormsPipe} from "../../../../pipes/legal-forms.pipe";
import {UsersListComponent} from "../../../util/users/users.component";
import {AddressInputComponent, applyDefaultCountry} from "../../../util/addressInput/address-input.component";
import {
  BicAccordionBodyComponent,
  BicAccordionComponent,
  BicAccordionElementComponent,
  BicAccordionHeaderComponent,
  BicButtonComponent,
  BicDropdownElementComponent,
  BicInputComponent,
  BicSelectComponent,
  BicToastService,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faChevronLeft, faChevronRight} from "@fortawesome/pro-solid-svg-icons";
import {PageHeaderComponent} from "../../../util/design/page-header/page-header.component";
import {
  austria,
  belgium,
  bulgaria,
  checkVAT,
  croatia,
  cyprus,
  czechRepublic,
  denmark,
  estonia,
  finland,
  france,
  germany,
  greece,
  hungary,
  ireland,
  italy,
  latvia,
  lithuania,
  luxembourg,
  malta,
  netherlands,
  poland,
  portugal,
  romania,
  slovakiaRepublic,
  slovenia,
  spain,
  sweden
} from 'jsvat';
import {NotificationsService} from '../../../../services/notifications/notifications.service';
import {getFormControlErrorMessage} from '../../../util/form-errors';
import {notBlankValidator} from '../../../../models/project-validators';
import LegalFormEnum = Organisation.LegalFormEnum;


@Component({
  selector: 'app-organisationList-create',
  templateUrl: './organisation-create.component.html',
  styleUrls: ['./organisation-create.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    KeyValuePipe,
    LegalFormsPipe,
    UsersListComponent,
    AddressInputComponent,
    BicInputComponent,
    BicSelectComponent,
    BicDropdownElementComponent,
    BicButtonComponent,
    BicAccordionHeaderComponent,
    BicAccordionBodyComponent,
    BicAccordionElementComponent,
    BicAccordionComponent,
    FaIconComponent,
    PageHeaderComponent,
    TooltipDirective
]
})
export class OrganisationCreateComponent implements OnInit, AfterViewInit{
  permissionService = inject(PermissionService);
  private formBuilder = inject(UntypedFormBuilder);
  private organisationControllerService = inject(OrganisationControllerService);
  private router = inject(Router);
  protected readonly faChevronLeft = faChevronLeft;
  private notificationsService = inject(NotificationsService);
  private stateConfigService = inject(StateConfigService);

  @Input() organisation: Organisation | undefined;

  organisationForm!: UntypedFormGroup;

  legalForm: typeof LegalFormEnum = LegalFormEnum;

  organisationChangedEvent: Subject<Organisation> = new Subject<Organisation>();

  expandedElement: number | null = null;

  countries = [
    {'jsvat': austria, 'code': 'AT', 'country': 'AUT'},
    {'jsvat': belgium, 'code': 'BE', 'country': 'BEL'},
    {'jsvat': bulgaria, 'code': 'BG', 'country': 'BGR'},
    {'jsvat': cyprus, 'code': 'CY', 'country': 'CYP'},
    {'jsvat': czechRepublic, 'code': 'CZ', 'country': 'CZE'},
    {'jsvat': germany, 'code': 'DE', 'country': 'DEU'},
    {'jsvat': denmark, 'code': 'DK', 'country': 'DNK'},
    {'jsvat': greece, 'code': 'GR', 'country': 'GRC'},
    {'jsvat': spain, 'code': 'ES', 'country': 'ESP'},
    {'jsvat': estonia, 'code': 'EE', 'country': 'EST'},
    {'jsvat': finland, 'code': 'FI', 'country': 'FIN'},
    {'jsvat': france, 'code': 'FR', 'country': 'FRA'},
    {'jsvat': croatia, 'code': 'HR', 'country': 'HRV'},
    {'jsvat': hungary, 'code': 'HU', 'country': 'HUN'},
    {'jsvat': ireland, 'code': 'IE', 'country': 'IRL'},
    {'jsvat': italy, 'code': 'IT', 'country': 'ITA'},
    {'jsvat': latvia, 'code': 'LV', 'country': 'LVA'},
    {'jsvat': lithuania, 'code': 'LT', 'country': 'LTU'},
    {'jsvat': luxembourg, 'code': 'LU', 'country': 'LUX'},
    {'jsvat': malta, 'code': 'MT', 'country': 'MLT'},
    {'jsvat': netherlands, 'code': 'NL', 'country': 'NLD'},
    {'jsvat': poland, 'code': 'PL', 'country': 'POL'},
    {'jsvat': portugal, 'code': 'PT', 'country': 'PRT'},
    {'jsvat': romania, 'code': 'RO', 'country': 'ROU'},
    {'jsvat': slovenia, 'code': 'SI', 'country': 'SVN'},
    {'jsvat': slovakiaRepublic, 'code': 'SK', 'country': 'SVK'},
    {'jsvat': sweden, 'code': 'SE', 'country': 'SWE'}
  ]


  ngAfterViewInit(): void {
    //otherwise Accordion header highlight bug
        this.expandedElement = null;
    }
  private bicToastService = inject(BicToastService);

  get nextDisabled(): boolean {
    if (!this.organisationForm) return true;
    const nameCtrl = this.organisationForm.get('name');
    const mailCtrl = this.organisationForm.get('organisationMail');
    const legalFormCtrl = this.organisationForm.get('legalForm');
    return !nameCtrl || !nameCtrl.valid || !mailCtrl || !mailCtrl.valid || !legalFormCtrl || !legalFormCtrl.valid;
  }

  fieldError(controlName: string): string | null {
    return getFormControlErrorMessage(this.organisationForm?.get(controlName));
  }

  get nextDisabledHint(): string {
    if (!this.nextDisabled) return '';
    const missing: string[] = [];
    if (!this.organisationForm?.get('name')?.valid) missing.push('Name');
    if (!this.organisationForm?.get('legalForm')?.valid) missing.push('Rechtsform');
    if (!this.organisationForm?.get('organisationMail')?.valid) missing.push('E-Mail');
    return 'Bitte prüfen: ' + missing.join(', ');
  }

  getCountry(countryName: String) {
    // @ts-ignore
    for (const country of this.countries) {
      if (countryName === country.country) {
        return country;
      }
    }
    return null;
  }

  isSubmitting = false;

  get createOrgDisabled(): boolean {
    return this.isSubmitting || !this.organisationForm?.valid;
  }

  ngOnInit(): void {
    this.organisationForm = this.formBuilder.group({
      id: new FormControl(""),
      name: new UntypedFormControl("", [Validators.required, notBlankValidator]),
      legalForm: new UntypedFormControl("", [Validators.required]),
      street: new UntypedFormControl("", [Validators.required]),
      houseNumber: new UntypedFormControl("", [Validators.required]),
      zipCode: new UntypedFormControl("", [Validators.required, Validators.pattern('^[0-9]{4,5}$')]),
      country: new UntypedFormControl("", [Validators.required]),
      city: new UntypedFormControl("", [Validators.required]),
      state: new UntypedFormControl("", [Validators.required]),
      organisationMail: new UntypedFormControl("", [Validators.required, Validators.email]),
      vatNumber: new UntypedFormControl("", [this.checkVATField])
    });

    // Disable state initially – the AddressInputComponent will enable it when country is DEU
    this.organisationForm.get('state')?.disable();

    this.organisationForm.patchValue(this.organisation!)
    if (this.organisation && !this.permissionService.hasOrganisationPrivilege('ORGANISATION_SETTINGS', this.organisation?.id!)){
      this.organisationForm.disable();
    }

    // Update vatNumber validator when country changes
    this.organisationForm.get('country')?.valueChanges.subscribe((newValue: String) => {
      const vatControl = this.organisationForm.get('vatNumber');
      const existingVatValue = vatControl?.value;

      if (!existingVatValue || this.countries.some((country: { code: any; }) => country.code === existingVatValue)) {
        const countryCode = this.getCountry(newValue)?.code
        vatControl?.setValue(countryCode);
      }
      // Trigger re-validation of VAT field
      vatControl?.updateValueAndValidity();
    });

    // nach der USt-Subscription, damit das Präfix wie bei manueller Auswahl gesetzt wird
    applyDefaultCountry(this.organisationForm.get('country'));
  }

  private checkVATField = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const country = this.getCountry(this.organisationForm?.get("country")?.value)
    if (value != undefined && value != "") {
      if (country && country.code != value && !checkVAT(value, [country.jsvat]).isValid) {
        return {validVATRequired: true};
      }
    }
    return null; // Validation passed
  }

  cancel() {
    this.router.navigate(['/organisations']);
  }

  save() {
    if (this.isSubmitting) {
      return;
    }
    const nameCtrl = this.organisationForm.get('name');
    if (typeof nameCtrl?.value === 'string') {
      nameCtrl.setValue(nameCtrl.value.trim(), {emitEvent: false});
    }
    const {id, ...formValue} = this.organisationForm.value;
    this.organisation = formValue;
    // Remove VAT number if it's just the country code
    if (this.organisation?.vatNumber && this.organisation.country && this.organisation.vatNumber == this.getCountry(this.organisation.country)?.code) {
      this.organisation.vatNumber = undefined
    }
    this.isSubmitting = true;
    this.organisationControllerService.createOrganisation(this.organisation!).subscribe({
      next: (organisation) => {
        this.organisation = organisation;
        void this.router.navigate(['/organisations']);
        // Die Privilegien der neuen Organisation stehen nicht im JWT, sondern kommen
        // aus GET /state und liegen in StateConfigService.organisations. Ohne diesen
        // Refresh haette der Ersteller bis zum Reload keine Rechte (Einstellungen
        // gesperrt, kein neues Projekt). Analog zur Projekt-Erstellung.
        this.stateConfigService.update().subscribe();
      },
      error: () => {
        this.isSubmitting = false;
        this.notificationsService.showErrorMessage('Organisation konnte nicht erstellt werden. Bitte erneut versuchen.');
      }
      });
  }
  protected readonly faChevronRight = faChevronRight;
}
